import { Box, Grid2 as Grid, Link, List, ListItem, ListItemText, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material'
import React from 'react'
import ReactMarkdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'

export default function MarkdownCard({ children }) {
  // Example from https://stackoverflow.com/questions/62923448/using-react-markdown-with-material-ui-table
  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw]}
      components={{
        iframe: ({ ...props }) => {
          try {
            const allowedDomains = [
              'www.youtube-nocookie.com',
              'www.youtube.com',
              'youtube.com',
              'player.vimeo.com',
              'vimeo.com',
              'www.bilibili.com',
              'player.bilibili.com',
              'www.meteoblue.com',
              'www.skaping.com',
              'www.onthesnow.com',
              'www.google.com',
              'maps.google.com',
            ]

            const url = props.src
            if (!url) return null

            const domain = new URL(url).hostname

            if (!allowedDomains.includes(domain)) {
              return (
                <Typography color="error" sx={{ my: 2 }}>
                  For security reasons, iframes are only allowed from: YouTube, Vimeo, Bilibili, Google Maps, meteoblue, skaping, and OnTheSnow
                </Typography>
              )
            }

            // Process URL for different platforms
            let processedUrl = url
            let additionalProps = {}

            if (domain.includes('youtube.com')) {
              // Convert any YouTube URL to embed format
              const videoId = url.match(
                // eslint-disable-next-line no-useless-escape
                /(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/user\/\S+|\/ytscreeningroom\?v=|\/sandalsResorts#\w\/\w\/.*\/))([^\/&\?]*).*/,
              )?.[1]
              if (videoId) {
                processedUrl = `https://www.youtube.com/embed/${videoId}`
                additionalProps = {
                  allow: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share',
                  frameBorder: '0',
                }
              }
            } else if (domain.includes('vimeo.com')) {
              // Convert Vimeo URL to embed format
              const videoId = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/)?.[1]
              if (videoId) {
                processedUrl = `https://player.vimeo.com/video/${videoId}`
                additionalProps = {
                  allow: 'autoplay; fullscreen; picture-in-picture',
                  frameBorder: '0',
                }
              }
            } else if (domain.includes('bilibili.com')) {
              // Handle Bilibili URLs
              additionalProps = {
                allow: 'fullscreen',
                frameBorder: '0',
                scrolling: 'no',
              }
            } else {
              additionalProps = {
                allow: 'fullscreen',
                frameBorder: '0',
                scrolling: 'yes',
              }
            }
            return (
              <Box sx={{ my: 2 }}>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    paddingTop: '56.25%', // 16:9 Aspect Ratio
                  }}
                >
                  <iframe
                    {...props}
                    {...additionalProps}
                    src={processedUrl}
                    title={`embedded-content-${domain}`}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: '1px solid rgba(0, 0, 0, 0.12)',
                      borderRadius: '4px',
                    }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="strict-origin-when-cross-origin"
                  />
                </Box>
              </Box>
            )
          } catch (error) {
            console.error('Error rendering iframe:', error)
            return (
              <Typography color="error" sx={{ my: 2 }}>
                Failed to load embedded content: {error.message}
              </Typography>
            )
          }
        },

        // *********
        // * Links *
        // *********
        a: ({ href, children }) => (
          <Link href={href} underline={'always'}>
            {children}
          </Link>
        ),

        // ********
        // * Text *
        // ********
        p: ({ children }) => <Typography sx={{ mt: 1 }}>{children}</Typography>,
        del: ({ children }) => (
          <Typography component="span" sx={{ mt: 1, textDecoration: 'line-through' }}>
            {children}
          </Typography>
        ),
        em: ({ children }) => (
          <Typography component="span" sx={{ mt: 1, fontStyle: 'italic' }}>
            {children}
          </Typography>
        ),
        strong: ({ children }) => (
          <Typography component="span" sx={{ mt: 1, fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        b: ({ children }) => (
          <Typography component="span" sx={{ mt: 1, fontWeight: 'bold' }}>
            {children}
          </Typography>
        ),
        h1: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h4'}>
            {children}
          </Typography>
        ),
        h2: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h5'}>
            {children}
          </Typography>
        ),
        h3: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h6'}>
            {children}
          </Typography>
        ),
        h4: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h7'}>
            {children}
          </Typography>
        ),
        h5: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h8'}>
            {children}
          </Typography>
        ),
        h6: ({ children }) => (
          <Typography gutterBottom sx={{ mt: 2 }} variant={'h9'}>
            {children}
          </Typography>
        ),

        // **********
        // * Tables *
        // **********
        table: ({ children }) => (
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">{children}</Table>
          </TableContainer>
        ),
        tbody: ({ children }) => <TableBody>{children}</TableBody>,
        // th: ({ children }) => (<TableHead>{children}</TableHead>),
        tr: ({ children }) => <TableRow>{children}</TableRow>,
        td: ({ children }) => (
          <TableCell>
            <Typography>{children}</Typography>
          </TableCell>
        ),

        // *********
        // * Lists *
        // *********
        ol: ({ children }) => (
          <Grid container justifyContent="center" alignItems="center">
            <Grid>
              <List
                sx={{
                  listStyleType: 'decimal',
                  mt: 2,
                  pl: 2,
                  '& .MuiListItem-root': {
                    display: 'list-item',
                  },
                }}
              >
                {children}
              </List>
            </Grid>
          </Grid>
        ),
        ul: ({ children }) => (
          <Grid container justifyContent="center" alignItems="center">
            <Grid>
              <List
                sx={{
                  listStyleType: 'disc',
                  mt: 2,
                  pl: 2,
                  '& .MuiListItem-root': {
                    display: 'list-item',
                  },
                }}
              >
                {children}
              </List>
            </Grid>
          </Grid>
        ),
        li: ({ children }) => (
          <ListItem sx={{ m: 0, p: 0, ml: 2 }} disableGutters>
            <ListItemText sx={{ pl: 0.25 }}>{children}</ListItemText>
          </ListItem>
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}
